/**
 * @author Iskra Popova
 *
 */

import { IViewConfigSchema, ILayersList } from "../baseConfigs/layoutConfigShema";
import SceneView from "../views/SceneView";
import BaseTransition from "../../src/transitions/BaseTransition";
import PercentScalableSceneView from "../views/PercentScalableSceneView";
import GameModel from "../../src/model/GameModel";
import App from "../App";
import Layout from "./Layout";
import Utils from "./Utils";
import ISceneOptions from "../contracts/ISceneOptions";
import { Container, Rectangle } from "pixi.js";
import { Timeline } from "../Timeline";
import GameEvents from "../const/GameEvents";

export default class Layer extends Container {

    protected activeSceneName: string;
    protected defaultSceneName: string;
    protected activeScene: SceneView;
    protected transitionScene: SceneView;
    protected scenes: string[];
    protected layersConfig: ILayersList;
    protected secundarylayersConfig: ILayersList;
    protected staticBound: Rectangle;
    protected overridenDefaultSceneName: string;
    private scenePool: Map<string, SceneView>;
    private smoothSceneTimeline: Timeline;

    constructor(scenes: string[], staticBound:Rectangle, layersConfig: ILayersList, defaultSceneName: string, secundarylayersConfig: ILayersList) {
        super();
        this.scenes = scenes;
        this.staticBound = staticBound;
        this.layersConfig = layersConfig;
        this.secundarylayersConfig = secundarylayersConfig;
        this.name = this.defaultSceneName = defaultSceneName;
        if (GameModel.isMobile() && this.hasScene(this.name + "-" + "mobile")) {
            this.overridenDefaultSceneName = this.name + "-" + "mobile";
        }

        this.scenePool = new Map<string, SceneView>();
        App.dispatcher.on(GameEvents.RESIZE, () => {
            if (this.smoothSceneTimeline) {
                this.smoothSceneTimeline.progress = 1;
                this.smoothSceneTimeline.kill();
                this.smoothSceneTimeline = undefined;
            }
        }, this);
    }

    public playDefault() {
        if (this.hasScene(this.defaultSceneName)) {
            this.setScene();
        }
    }

    public findSceneNameContainsBound(boundName: string): string {
        let sceneName = "";
        let reGex = /[\w]+~[\d]+/g
        Object.entries(this.layersConfig).forEach(([key, vl]) => {
            if (vl.groups && Object.entries(vl.groups).length > 0) {
                Object.entries(vl.groups).forEach(([gKey, gValue]) => {
                    gValue.bounds.forEach(bound => {
                        if (bound.id === boundName) {
                            let match = key.match(reGex);
                            if (match && match.length > 0) {
                                sceneName = match[0] || "";
                            }
                        }
                    })
                })
            }
        })

        return sceneName
    }

    public getDefaultSceneName(): string {
        return this.defaultSceneName;
    }

    public getCurrentScene(): SceneView {
        return this.activeScene;
    }

    public getCurrentTransitionScene(): SceneView {
        return this.transitionScene;
    }

    public transitionToScene(sceneName: string, transition: BaseTransition, onIn?: (...args: any[]) => void, onComplete?: (...args: any[]) => void  ) {
        if (!sceneName) {
            sceneName = this.defaultSceneName;
        } else if (sceneName !== this.defaultSceneName) {
            sceneName = this.defaultSceneName + "-" + sceneName;
        }

        this.transitionScene = this.createScene(sceneName);
        this.transitionScene.alpha = 0;
        this.addChild(this.transitionScene);
        // const tl = transition.execute();
        transition.in().then(() => {
            if (onIn) {
                onIn();
            }
            return transition.out();
        }).then( () => {
            this.clear();
            this.activeScene = this.transitionScene;
            this.activeScene.alpha = 1;
            // this.addChild(this.transitionScene);
            this.transitionScene = null;
            if (onComplete) {
                onComplete();
            }
        });
    }

    /**
     * Switches the current scene with other one, using alpha transition.
     * No transition will be performed in case the active scene name mathes the passed one, or there is no active scene.
     *
     * @param {string} [sceneName=undefined]
     * @param {number} [durationInSeconds=2.0]
     * @return {*}  {SceneView}
     * @memberof Layer
     */
    public setSceneSmooth(sceneName: string = undefined, durationInSeconds: number = 2.0): SceneView {
        if (!this.activeScene) {
            // No transition needed.
            return this.setScene(sceneName);
        }

        if (!sceneName) {
            sceneName = this.defaultSceneName;
        } else if (sceneName !== this.defaultSceneName) {
            sceneName = this.defaultSceneName + "-" + sceneName;
        }

        if (this.activeSceneName === sceneName && this.activeScene) {
            // No transition needed.
            return this.activeScene;
        }

        let newScene = this.createScene(sceneName);
        newScene.alpha = 0;
        this.addChild(newScene);

        this.smoothSceneTimeline = new Timeline();
        this.smoothSceneTimeline.to(newScene, durationInSeconds, { alpha: 1.0 }, 0);
        this.smoothSceneTimeline.to(this.activeScene, durationInSeconds, { alpha: 0 }, 0);
        this.smoothSceneTimeline.call(() => { this.activeScene = newScene; this.activeSceneName = sceneName; }, [], this);

        return newScene;
    }

    public getScenesTypeCount(sceneName: string): number {
        let count: number = 0;
        this.scenes.findIndex(element => {
            if(element.includes(sceneName)) {
                count++
            }
        });
        return count;
    }

    public setScene(sceneName?: string, options?: ISceneOptions, reusable: boolean = false): SceneView {
        if (!sceneName) {
            sceneName = this.defaultSceneName;
            if (this.overridenDefaultSceneName) {
                sceneName = this.overridenDefaultSceneName;
            }
        } else if (sceneName !== this.defaultSceneName) {
            sceneName = this.defaultSceneName + "-" + sceneName;
        }

        if (!this.hasScene(sceneName)) {
            throw new Error(("No scene found with name " + sceneName + " in layer "));
        }

        if (this.activeSceneName === sceneName && this.activeScene && !this.activeScene.destroyed) {
            return this.activeScene;
        } else {
            this.clear();
        }

        // const sceneConfig = this.layersConfig[sceneName];

        this.activeScene = this.createScene(sceneName, options, reusable);
        this.activeSceneName = sceneName;
        if (!this.activeScene.isReused) {
            this.addChild(this.activeScene);
        }

        // if (this.activeScene && this.activeScene instanceof PercentScalableSceneView) {
        //     this.x = 0;
        //     this.y = 0;
        //     this.activeScene.reposition();
        // }
        this.reposition();

        return this.activeScene;
    }

    public hasScene(sceneName: string): boolean {
        return (this.scenes.indexOf(sceneName) !== -1) || (this.scenes.indexOf(sceneName + "~%") !== -1)
    }

    protected isPercentScalable(sceneName: string): boolean {
        return (this.scenes.indexOf(sceneName + "~%") !== -1)
    }

    protected getSceneConfig(sceneName: string): IViewConfigSchema {
        return this.layersConfig[sceneName] || this.layersConfig[sceneName + "~%"];
    }


    protected getPortraitConfig(sceneName: string): IViewConfigSchema {
        if (GameModel.isMobile() && this.secundarylayersConfig ) {
            return this.secundarylayersConfig[sceneName] || this.secundarylayersConfig[sceneName + "~%"];
        }

        return null;
    }

    public getOrphanedScene(sceneName: string): SceneView {
        if (!sceneName) {
            sceneName = this.defaultSceneName;
        } else if (sceneName !== this.defaultSceneName) {
            sceneName = this.defaultSceneName + "-" + sceneName;
        }
        let scene = this.createScene(sceneName);
        return scene;
    }

    protected createScene(sceneName: string, options?: ISceneOptions, reusable: boolean = false): SceneView {
        const sceneKey = this.getSceneKey(sceneName);
        if (reusable) {
            const sceneFromPool = this.tryGetSceneFromPool(sceneKey);
            if (sceneFromPool) {
                sceneFromPool.visible = true;
                sceneFromPool.renderable = true;
                sceneFromPool.setReuseStatus(true, true);
                return sceneFromPool;
            }
        }

        const sceneConfig = this.getSceneConfig(sceneName);
        const portraitConfig = this.getPortraitConfig(sceneName);
        const scene = this.isPercentScalable(sceneName) ? new PercentScalableSceneView(sceneConfig,portraitConfig,options) : new SceneView(sceneConfig, portraitConfig,options);
        scene.name = sceneName;
        scene.setReuseStatus(reusable, false);
        if (reusable) {
            this.scenePool.set(sceneKey, scene);
        }
        
        return scene;
    }

    public clear() {
        // this.removeChildren();
        if (this.activeScene) {
            if (!this.activeScene.isReusable) {
                this.activeScene.removeChildren();
                this.activeScene.destroy();
                this.activeScene = null;
            } else {
                this.activeScene.interactive = false;
                this.activeScene.visible = false;
                this.activeScene.renderable = false;
            }
        }
        this.activeSceneName = null;
    }

    public completeClear() {
        this.removeChildren();
        if (this.activeScene) {
            if (!this.activeScene.isReusable) {
            this.activeScene.removeChildren();
            this.activeScene.destroy();
            this.activeScene = null;
            } else {
                this.activeScene.interactive = false;
                this.activeScene.visible = false;
                this.activeScene.renderable = false;
            }
        }
        if (this.transitionScene) {
            this.transitionScene.removeChildren();
            this.transitionScene.destroy();
            this.transitionScene = null;
        }
        this.activeSceneName = null;

    }

    public reposition(orientationChanged = false) {
        const rect = Utils.deepClone(this.staticBound) ;
        if (!rect) {
            return;
        }
        const canvasWidth = Layout.canvasWidth;
        const canvasHeight = Layout.canvasHeight;
        rect.x = (canvasWidth / App.instance.stage.scale.x ) / 2 - Layout.scalePixels(rect.width) / 2;
        rect.y = (canvasHeight / App.instance.stage.scale.x ) / 2 - Layout.scalePixels(rect.height) / 2;
        if (this.activeScene && this.activeScene instanceof PercentScalableSceneView) {
            this.x = 0;
            this.y = 0;
            if (!orientationChanged) {
                this.activeScene.reposition();
            }
        } else {
            this.x = rect.x;
            this.y = rect.y;
        }
    }

    private getSceneKey(sceneName: string) {
        return sceneName + (Layout.isLandscape() ? "_landscape" : "_portrait");
    }

    private tryGetSceneFromPool(sceneKey: string, deleteDestroyed: boolean = true): SceneView {
        if (this.scenePool.has(sceneKey)) {
            const scene = this.scenePool.get(sceneKey);
            if (scene) {
                if (!App.isObjectDestroyed(scene)) {
                    return scene
                } else {
                    if (deleteDestroyed) {
                        this.scenePool.delete(sceneKey);
                    }
                }
            }
        }

        return undefined;
    }
}